import { UseSuspenseQueryOptions, useMutation, useQueryClient } from '@tanstack/react-query';

import { getCurrentUserOptions } from '@/module/Security/model/SecurityQuery';

import { useCurrentUserStore } from '@feature/security';
import { getQueryKey } from '@feature/utils';

import * as api from './UniversityConfigApi';
import { UniversityConfig, UniversityConfigRequest } from './UniversityConfigTypes';

const universityConfigKey = getQueryKey('universityConfig');

export function getUniversityConfigOptions(): UseSuspenseQueryOptions<UniversityConfig> {
  return {
    queryKey: universityConfigKey.as(['one']),
    queryFn: () => api.get(),
  };
}

export function useUniversityConfigUpdate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (request: UniversityConfigRequest) => api.update(request),
    // Saving can grant or revoke the saver's own roles, and nothing observes the user query, so it is fetched into the store.
    onSuccess: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: universityConfigKey.root() }),
        queryClient.fetchQuery({ ...getCurrentUserOptions(), staleTime: 0 }).then(
          user => useCurrentUserStore.getState().setCurrentUser(user),
          // The config is already saved; dropping the cached user makes the next route load fetch it instead of reusing it.
          () => queryClient.removeQueries({ queryKey: getCurrentUserOptions().queryKey }),
        ),
      ]),
  });
}
